// ✅ src/pages/VehicleV2/VehicleStatusReport.jsx
// UPDATED — Production listing template
// 3-column filters • overflow locked • important list columns

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/vehicleStatusReport";

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const formatMoney = (v) =>
  Number(toNum(v, 0)).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatNumber = (v) =>
  Number(toNum(v, 0)).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const exportToCsv = (rows, filename = "vehicle-status-report.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0] || {});
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => `"${String(r?.[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const mapStatusForAspx = (vehicleStatus) => {
  const s = toStr(vehicleStatus);
  if (!s) return "Select";
  return s;
};

const createDefaultFilters = (branchType, branchId) => ({
  branch: branchType === "HO" ? "Select" : String(branchId),
  regNo: "",
  month: "Select",
  year: "Select",
  vehicleCode: "",
  vehicleName: "",
  vehicleMainType: "Select",
  vehicleStatus: "Select",
});

const buildPrintQuery = (obj) => {
  const sp = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    sp.set(k, String(v));
  });
  return sp.toString();
};

export default function VehicleStatusReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const didInitRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading;
  const [showFilters, setShowFilters] = useState(true);
  const [vehicleType, setVehicleType] = useState("own");
  const vehMode = vehicleType === "hire" ? "H" : "O";
  const [filters, setFilters] = useState(defaultFilters);
  const [branches, setBranches] = useState([]);
  const [mainTypes, setMainTypes] = useState([]);
  const [years, setYears] = useState([]);
  const [statuses, setStatuses] = useState(["Running", "Idle", "Maintenance"]);
  const months = useMemo(
    () => ["January","February","March","April","May","June","July","August","September","October","November","December"],
    []
  );
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  const selectedBranchName = useMemo(() => {
    const selected = branches.find((b) => String(b.id) === String(filters.branch));
    return selected?.name || "";
  }, [branches, filters.branch]);

  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);
      const res = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });
      const data = res.data || {};
      if (!data?.success) throw new Error(data?.message || "Meta load failed");
      setBranches(toList(data.branches));
      setMainTypes(toList(data.vehicleMainTypes));
      if (toList(data.years).length) {
        setYears(data.years);
      } else {
        const yNow = new Date().getFullYear();
        const ys = [];
        for (let y = 2006; y <= yNow; y++) ys.push(String(y));
        setYears(ys);
      }
      setStatuses(toList(data.statuses).length ? data.statuses : ["Running", "Idle", "Maintenance"]);
      if (session.branchType !== "HO") {
        setFilters((prev) => ({ ...prev, branch: String(session.branchId) }));
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdown meta");
      setBranches([]);
      setMainTypes([]);
      setYears([]);
      setStatuses(["Running", "Idle", "Maintenance"]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, session.loginUserId, commonHeaders]);

  const buildListParams = useCallback(
    (f = filters, nextVehMode = vehMode) => ({
      branchType: session.branchType,
      branchId: session.branchId,
      vehicleMode: nextVehMode,
      branch: session.branchType === "HO" ? (isSelect(f.branch) ? "Select" : toStr(f.branch)) : String(session.branchId),
      regNo: toStr(f.regNo),
      month: isSelect(f.month) ? "Select" : toStr(f.month),
      year: isSelect(f.year) ? "Select" : toStr(f.year),
      vehicleCode: toStr(f.vehicleCode),
      vehicleName: toStr(f.vehicleName),
      vehicletype: isSelect(f.vehicleMainType) ? "Select" : toStr(f.vehicleMainType),
      status: isSelect(f.vehicleStatus) ? "Select" : toStr(f.vehicleStatus),
    }),
    [filters, vehMode, session.branchType, session.branchId]
  );

  const loadList = useCallback(
    async (nextFilters = filters, nextVehMode = vehMode, silent = false) => {
      try {
        setListLoading(true);
        const res = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(nextFilters, nextVehMode),
          headers: commonHeaders,
        });
        const data = res.data || {};
        if (!data?.success) throw new Error(data?.message || "Failed to load report");
        const list = toList(data.rows);
        setRows(list);
        setCurrentPage(1);
        if (!silent) toast.success(`Found ${list.length} records`);
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, vehMode, buildListParams, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      const baseFilters = createDefaultFilters(session.branchType, session.branchId);
      setFilters(baseFilters);
      setRows([]);
      setCurrentPage(1);
      await loadMeta();
      await loadList(baseFilters, vehMode, true);
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadMeta, loadList, vehMode]);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback((e) => {
    setField(e.target.name, e.target.value);
  }, [setField]);

  const handleVehicleTypeChange = useCallback(async (nextType) => {
    const nextVehMode = nextType === "hire" ? "H" : "O";
    setVehicleType(nextType);
    await loadList(filters, nextVehMode, true);
  }, [filters, loadList]);

  const handleSearch = useCallback(async () => {
    await loadList(filters, vehMode);
  }, [filters, vehMode, loadList]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await loadList(reset, vehMode);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, vehMode, loadList]);

  const handleReset = useCallback(() => {
    setFilters(createDefaultFilters(session.branchType, session.branchId));
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleExport = useCallback(() => {
    if (!rows.length) return toast.info("No rows to export");
    exportToCsv(rows, "vehicle-status-report.csv");
    toast.success("Exported successfully");
  }, [rows]);

  const buildAspxPrintParams = useCallback(() => {
    const f = filters;
    const monthVal = isSelect(f.month) ? "Select" : toStr(f.month);
    const yearVal = isSelect(f.year) ? "Select" : toStr(f.year);
    const mainTypeVal = isSelect(f.vehicleMainType) ? "Select" : toStr(f.vehicleMainType);
    const statusVal = mapStatusForAspx(isSelect(f.vehicleStatus) ? "" : f.vehicleStatus);
    return {
      branchType: session.branchType,
      branchId: session.branchId,
      userId: session.userId,
      vehMode,
      branch: session.branchType === "HO" ? selectedBranchName || "Select" : selectedBranchName || "Select",
      regNo: toStr(f.regNo),
      vehCode: toStr(f.vehicleCode),
      vehName: toStr(f.vehicleName),
      month: monthVal,
      year: yearVal,
      vehtype: mainTypeVal,
      vehicletype: mainTypeVal,
      vehicleMainType: mainTypeVal,
      vehicleStatus: statusVal,
      status: statusVal,
    };
  }, [filters, session.branchType, session.branchId, session.userId, vehMode, selectedBranchName]);

  const goPrintMain = useCallback(() => {
    const params = buildAspxPrintParams();
    navigate(`/vehicle/VehicleStatusReport_Print?${buildPrintQuery(params)}`, { state: params });
  }, [buildAspxPrintParams, navigate]);

  const goPrintSeparate = useCallback(() => {
    const params = buildAspxPrintParams();
    navigate(`/vehicle/VehicleStatusReport_SeperatePrint?${buildPrintQuery(params)}`, { state: params });
  }, [buildAspxPrintParams, navigate]);

  const goPrintBranchwise = useCallback(() => {
    const params = buildAspxPrintParams();
    navigate(`/vehicle/VehicleStatusBranchWiseReport_Print?${buildPrintQuery(params)}`, { state: params });
  }, [buildAspxPrintParams, navigate]);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = rows.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = useCallback((p) => setCurrentPage(Math.min(Math.max(1, p), totalPages)), [totalPages]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  const statusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (s.includes("run")) return isDark ? "text-green-400" : "text-green-600";
    if (s.includes("idle")) return isDark ? "text-yellow-400" : "text-yellow-600";
    if (s.includes("main")) return isDark ? "text-red-400" : "text-red-600";
    return isDark ? "text-gray-200" : "text-gray-700";
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">VEHICLE STATUS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Vehicle Status Report</h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} • User ID: {session.loginUserId || "-"}
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={goPrintMain} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaPrint /> Print</button>
              <button onClick={goPrintSeparate} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaPrint /> Separate</button>
              <button onClick={goPrintBranchwise} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaPrint /> Branchwise</button>
              <button onClick={handleExport} disabled={isBusy || rows.length === 0} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}><FaFileExport /> Export</button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={handleBack} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((p) => !p)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleReset} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} type="button" disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} type="button" disabled={isBusy} className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${isBusy ? "opacity-60 cursor-not-allowed" : ""} ${buttonClass} ${hoverClass} text-white`}><FaSearch /> {listLoading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="flex gap-6 mb-4 flex-wrap">
                <label className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input type="radio" checked={vehicleType === "own"} onChange={() => handleVehicleTypeChange("own")} disabled={isBusy} />
                  Own Vehicle
                </label>
                <label className={`flex items-center gap-2 text-sm ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input type="radio" checked={vehicleType === "hire"} onChange={() => handleVehicleTypeChange("hire")} disabled={isBusy} />
                  Hire Vehicle
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={isBusy || session.branchType !== "HO"}>
                    <option value="Select">Select Branch</option>
                    {branches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input type="text" name="regNo" value={filters.regNo} onChange={handleChange} className={fieldClass} placeholder="Enter registration number" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select Month</option>
                    {months.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select Year</option>
                    {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle code" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Name</label>
                  <input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle name" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select Vehicle Main Type</option>
                    {mainTypes?.length ? mainTypes.map((t) => <option key={t.id} value={t.name}>{t.name}</option>) : (
                      <>
                        <option value="TIPPER MAIN">TIPPER MAIN</option>
                        <option value="EXCAVATOR">EXCAVATOR</option>
                        <option value="DUMP TRUCK">DUMP TRUCK</option>
                        <option value="CRANE">CRANE</option>
                        <option value="LOADER">LOADER</option>
                        <option value="TRAILER">TRAILER</option>
                        <option value="BUS">BUS</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Status</label>
                  <select name="vehicleStatus" value={filters.vehicleStatus} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select Status</option>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {rows.length}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : currentItems.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Vehicle Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Try adjusting your search criteria or click View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Reg No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Purchase Value</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Cum. Hr</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((item, idx) => (
                    <tr key={item.id ?? idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={tdClass}>{indexOfFirstItem + idx + 1}</td>
                      <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{toStr(item.fld_vehicle_name) || "-"}</td>
                      <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{toStr(item.fld_vehicle_regno) || "-"}</td>
                      <td className={`px-4 py-3 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>₹{formatMoney(item.fld_purchased_amt)}</td>
                      <td className={tdClass}>{formatNumber(item.fld_cummulativehr)}</td>
                      <td className={`px-4 py-3 text-sm font-semibold ${statusColor(item.status)}`}>{toStr(item.fld_status) || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={2500} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}